/**
 * ScheduleSurface — TV-RV-11
 *
 * Front-desk scheduling surface. Allows front-desk staff to:
 *   1. Check provider availability for a date range
 *   2. Book a new appointment (returns patient join link)
 *   3. View upcoming appointments (date filter)
 *   4. Reschedule or cancel an existing appointment
 *
 * ALL mutations route through sandman /api/v1/scheduling — never
 * directly to FHIR or Firestore. Sandman handles FHIR Appointment
 * creation, PG write, and the patient join-link token.
 *
 * Route: /schedule (front-desk only, guarded in App.jsx)
 * Auth: uses token from useAuth()
 * Base URL: VITE_SANDMAN_URL (falls back to https://sandman.collabkare.io)
 */

import React, { useState } from 'react';
import { Card, Badge } from '@/components';
import {
  Calendar,
  Clock,
  User,
  Search,
  Plus,
  RefreshCw,
  Link,
  X,
  Loader2,
  CheckCircle,
  AlertCircle,
  CalendarX,
} from 'lucide-react';
import { useAuth } from '@/auth/AuthProvider';
import {
  getProviderAvailability,
  bookAppointment,
  getAppointments,
  rescheduleAppointment,
  cancelAppointment,
} from './schedulingService';

// ── Helpers ──────────────────────────────────────────────────────────────────

const today = () => new Date().toISOString().slice(0, 10);
const oneWeekOut = () => {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d.toISOString().slice(0, 10);
};

function toastMessage(msg, isError = false) {
  // Lightweight inline feedback; replace with a Toast lib if one exists.
  console[isError ? 'error' : 'info'](`[ScheduleSurface] ${msg}`);
}

// ── Sub-components ─────────────────────────────────────────────────────────

/** Inline status banner */
function StatusBanner({ message, variant = 'success' }) {
  const styles = {
    success: 'bg-green-500/10 border-green-500/30 text-green-300',
    error: 'bg-red-500/10 border-red-500/30 text-red-300',
    info: 'bg-blue-500/10 border-blue-500/30 text-blue-300',
  };
  const Icon = variant === 'success' ? CheckCircle : variant === 'error' ? AlertCircle : Clock;
  return (
    <div className={`flex items-start gap-2 p-3 rounded-lg border text-sm ${styles[variant]}`}>
      <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" />
      <span>{message}</span>
    </div>
  );
}

/** Read-only join link display */
function JoinLinkCard({ label, link }) {
  if (!link) return null;
  return (
    <div className="flex items-center gap-2 p-3 bg-slate-800 border border-slate-700 rounded-lg">
      <Link className="w-4 h-4 text-blue-400 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-slate-400 mb-0.5">{label}</p>
        <a
          href={link}
          target="_blank"
          rel="noreferrer"
          className="text-xs font-mono text-blue-400 hover:underline truncate block"
        >
          {link}
        </a>
      </div>
      <button
        onClick={() => navigator.clipboard?.writeText(link)}
        className="text-xs text-slate-400 hover:text-white px-2 py-1 rounded bg-slate-700 hover:bg-slate-600 transition-colors"
      >
        Copy
      </button>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export default function ScheduleSurface() {
  const { token } = useAuth();

  // ── Tab ────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('book'); // 'book' | 'manage'

  // ── Availability check ─────────────────────────────────────────────────
  const [avail, setAvail] = useState({ providerId: '', from: today(), to: oneWeekOut() });
  const [slots, setSlots] = useState(null);
  const [availLoading, setAvailLoading] = useState(false);
  const [availError, setAvailError] = useState(null);

  const handleCheckAvailability = async () => {
    if (!avail.providerId.trim()) {
      setAvailError('Provider ID is required.');
      return;
    }
    setAvailLoading(true);
    setAvailError(null);
    setSlots(null);
    try {
      const data = await getProviderAvailability(token, avail);
      setSlots(data?.slots || []);
    } catch (err) {
      setAvailError(err.message);
    } finally {
      setAvailLoading(false);
    }
  };

  // ── Book appointment ───────────────────────────────────────────────────
  const [bookForm, setBookForm] = useState({
    providerId: '',
    patientId: '',
    slotId: '',
    start: '',
    end: '',
    chiefComplaint: '',
  });
  const [bookResult, setBookResult] = useState(null);
  const [bookLoading, setBookLoading] = useState(false);
  const [bookError, setBookError] = useState(null);

  const handleBook = async (e) => {
    e.preventDefault();
    if (!bookForm.providerId || !bookForm.patientId || !bookForm.start || !bookForm.end) {
      setBookError('Provider ID, Patient ID, Start, and End are required.');
      return;
    }
    setBookLoading(true);
    setBookError(null);
    setBookResult(null);
    try {
      const result = await bookAppointment(token, bookForm);
      setBookResult(result);
      setBookForm({ providerId: '', patientId: '', slotId: '', start: '', end: '', chiefComplaint: '' });
    } catch (err) {
      setBookError(err.message);
    } finally {
      setBookLoading(false);
    }
  };

  // ── Manage appointments ────────────────────────────────────────────────
  const [manageFilter, setManageFilter] = useState({ date: today(), patientId: '', providerId: '' });
  const [apptList, setApptList] = useState(null);
  const [manageLoading, setManageLoading] = useState(false);
  const [manageError, setManageError] = useState(null);
  const [actionApptId, setActionApptId] = useState(null);
  const [rescheduleForm, setRescheduleForm] = useState({ start: '', end: '' });
  const [cancelReason, setCancelReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionStatus, setActionStatus] = useState(null); // { message, variant }

  const handleLoadAppts = async () => {
    setManageLoading(true);
    setManageError(null);
    setApptList(null);
    setActionApptId(null);
    setActionStatus(null);
    try {
      const data = await getAppointments(token, {
        date: manageFilter.date || undefined,
        patientId: manageFilter.patientId || undefined,
        providerId: manageFilter.providerId || undefined,
      });
      setApptList(data?.appointments || []);
    } catch (err) {
      setManageError(err.message);
    } finally {
      setManageLoading(false);
    }
  };

  const handleReschedule = async (apptId) => {
    if (!rescheduleForm.start || !rescheduleForm.end) {
      setActionStatus({ message: 'New start and end times are required.', variant: 'error' });
      return;
    }
    setActionLoading(true);
    setActionStatus(null);
    try {
      await rescheduleAppointment(token, apptId, rescheduleForm);
      setActionStatus({ message: `Appointment ${apptId} rescheduled.`, variant: 'success' });
      setActionApptId(null);
      setRescheduleForm({ start: '', end: '' });
      handleLoadAppts();
    } catch (err) {
      setActionStatus({ message: err.message, variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async (apptId) => {
    setActionLoading(true);
    setActionStatus(null);
    try {
      await cancelAppointment(token, apptId, cancelReason);
      setActionStatus({ message: `Appointment ${apptId} cancelled.`, variant: 'success' });
      setActionApptId(null);
      setCancelReason('');
      handleLoadAppts();
    } catch (err) {
      setActionStatus({ message: err.message, variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────

  const tabs = [
    { id: 'book', label: 'Book / Availability' },
    { id: 'manage', label: 'Manage Appointments' },
  ];

  return (
    <div className="min-h-screen bg-slate-950 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white mb-1">Scheduling</h1>
          <p className="text-slate-400">
            Book, reschedule, and manage appointments via the CollabKare scheduling service.
          </p>
        </div>

        {/* Tab bar */}
        <div className="flex gap-1 mb-6 bg-slate-900 border border-slate-800 rounded-xl p-1">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeTab === t.id
                  ? 'bg-slate-700 text-white'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ── TAB: Book / Availability ─────────────────────────────────── */}
        {activeTab === 'book' && (
          <div className="grid grid-cols-2 gap-6">
            {/* Check availability */}
            <Card className="bg-slate-900 border-slate-800 p-5">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Search className="w-4 h-4 text-blue-400" />
                Check Availability
              </h2>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Provider ID</label>
                  <input
                    type="text"
                    value={avail.providerId}
                    onChange={(e) => setAvail((p) => ({ ...p, providerId: e.target.value }))}
                    placeholder="Practitioner/dr-001"
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">From</label>
                    <input
                      type="date"
                      value={avail.from}
                      onChange={(e) => setAvail((p) => ({ ...p, from: e.target.value }))}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">To</label>
                    <input
                      type="date"
                      value={avail.to}
                      onChange={(e) => setAvail((p) => ({ ...p, to: e.target.value }))}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
                    />
                  </div>
                </div>
                <button
                  onClick={handleCheckAvailability}
                  disabled={availLoading}
                  className="w-full flex items-center justify-center gap-2 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  {availLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  {availLoading ? 'Checking…' : 'Check Availability'}
                </button>
                {availError && <StatusBanner message={availError} variant="error" />}
              </div>

              {/* Slot results */}
              {slots !== null && (
                <div className="mt-4">
                  <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">
                    Available Slots ({slots.length})
                  </p>
                  {slots.length === 0 ? (
                    <p className="text-xs text-slate-500 italic">No available slots in this range.</p>
                  ) : (
                    <div className="space-y-1.5 max-h-52 overflow-y-auto pr-1">
                      {slots.map((slot, idx) => (
                        <div
                          key={slot.id || idx}
                          className="flex items-center justify-between p-2 bg-slate-800 border border-slate-700 rounded-lg"
                        >
                          <div className="flex items-center gap-2">
                            <Clock className="w-3.5 h-3.5 text-blue-400" />
                            <span className="text-xs text-white">
                              {slot.start
                                ? new Date(slot.start).toLocaleString([], {
                                    month: 'short',
                                    day: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })
                                : slot.id}
                            </span>
                          </div>
                          <button
                            onClick={() =>
                              setBookForm((p) => ({
                                ...p,
                                slotId: slot.id || '',
                                start: slot.start || '',
                                end: slot.end || '',
                              }))
                            }
                            className="text-xs text-blue-400 hover:text-blue-300 font-medium"
                          >
                            Select
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Card>

            {/* Book appointment form */}
            <Card className="bg-slate-900 border-slate-800 p-5">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Plus className="w-4 h-4 text-green-400" />
                Book Appointment
              </h2>

              {bookResult ? (
                <div className="space-y-3">
                  <StatusBanner message="Appointment booked successfully." variant="success" />
                  <div className="p-3 bg-slate-800 border border-slate-700 rounded-lg">
                    <p className="text-xs text-slate-400 mb-1">Appointment ID</p>
                    <p className="font-mono text-sm text-white">{bookResult.appointmentId || '—'}</p>
                  </div>
                  <JoinLinkCard label="Patient Join Link" link={bookResult.patientJoinLink} />
                  <JoinLinkCard label="Provider Join Link" link={bookResult.providerJoinLink} />
                  <button
                    onClick={() => setBookResult(null)}
                    className="w-full py-2 text-sm text-slate-300 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors"
                  >
                    Book Another
                  </button>
                </div>
              ) : (
                <form onSubmit={handleBook} className="space-y-3">
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Provider ID *</label>
                    <input
                      type="text"
                      value={bookForm.providerId}
                      onChange={(e) => setBookForm((p) => ({ ...p, providerId: e.target.value }))}
                      placeholder="Practitioner/dr-001"
                      required
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Patient ID *</label>
                    <input
                      type="text"
                      value={bookForm.patientId}
                      onChange={(e) => setBookForm((p) => ({ ...p, patientId: e.target.value }))}
                      placeholder="Patient/patient-001"
                      required
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Start *</label>
                      <input
                        type="datetime-local"
                        value={bookForm.start}
                        onChange={(e) => setBookForm((p) => ({ ...p, start: e.target.value }))}
                        required
                        className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">End *</label>
                      <input
                        type="datetime-local"
                        value={bookForm.end}
                        onChange={(e) => setBookForm((p) => ({ ...p, end: e.target.value }))}
                        required
                        className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
                      />
                    </div>
                  </div>
                  {bookForm.slotId && (
                    <p className="text-xs text-slate-400 italic">
                      Slot: <span className="font-mono text-slate-300">{bookForm.slotId}</span>
                    </p>
                  )}
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Chief Complaint</label>
                    <input
                      type="text"
                      value={bookForm.chiefComplaint}
                      onChange={(e) => setBookForm((p) => ({ ...p, chiefComplaint: e.target.value }))}
                      placeholder="Reason for visit"
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  {bookError && <StatusBanner message={bookError} variant="error" />}
                  <button
                    type="submit"
                    disabled={bookLoading}
                    className="w-full flex items-center justify-center gap-2 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    {bookLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calendar className="w-4 h-4" />}
                    {bookLoading ? 'Booking…' : 'Book Appointment'}
                  </button>
                </form>
              )}
            </Card>
          </div>
        )}

        {/* ── TAB: Manage Appointments ─────────────────────────────────── */}
        {activeTab === 'manage' && (
          <div className="space-y-5">
            {/* Filter bar */}
            <Card className="bg-slate-900 border-slate-800 p-5">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <RefreshCw className="w-4 h-4 text-slate-400" />
                Load Appointments
              </h2>
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Date</label>
                  <input
                    type="date"
                    value={manageFilter.date}
                    onChange={(e) => setManageFilter((p) => ({ ...p, date: e.target.value }))}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Patient ID (optional)</label>
                  <input
                    type="text"
                    value={manageFilter.patientId}
                    onChange={(e) => setManageFilter((p) => ({ ...p, patientId: e.target.value }))}
                    placeholder="Patient/…"
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Provider ID (optional)</label>
                  <input
                    type="text"
                    value={manageFilter.providerId}
                    onChange={(e) => setManageFilter((p) => ({ ...p, providerId: e.target.value }))}
                    placeholder="Practitioner/…"
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                  />
                </div>
              </div>
              <button
                onClick={handleLoadAppts}
                disabled={manageLoading}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
              >
                {manageLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                {manageLoading ? 'Loading…' : 'Load'}
              </button>
              {manageError && <div className="mt-3"><StatusBanner message={manageError} variant="error" /></div>}
              {actionStatus && <div className="mt-3"><StatusBanner message={actionStatus.message} variant={actionStatus.variant} /></div>}
            </Card>

            {/* Appointment list */}
            {apptList !== null && (
              <Card className="bg-slate-900 border-slate-800 p-5">
                <h3 className="text-sm font-semibold text-slate-300 mb-3">
                  {apptList.length} appointment{apptList.length !== 1 ? 's' : ''}
                </h3>
                {apptList.length === 0 ? (
                  <div className="flex flex-col items-center py-8 text-slate-500">
                    <CalendarX className="w-6 h-6 mb-2" />
                    <p className="text-sm">No appointments match these filters.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {apptList.map((appt) => (
                      <div
                        key={appt.id || appt.appointmentId}
                        className="p-4 bg-slate-800 border border-slate-700 rounded-lg"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-medium text-white text-sm">
                              {appt.patientName || appt.patientId || 'Unknown patient'}
                            </p>
                            <p className="text-xs text-slate-400">
                              {appt.start
                                ? new Date(appt.start).toLocaleString([], {
                                    weekday: 'short',
                                    month: 'short',
                                    day: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })
                                : '—'}
                              {appt.end
                                ? ` – ${new Date(appt.end).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                                : ''}
                            </p>
                            {appt.chiefComplaint && (
                              <p className="text-xs text-slate-500 mt-0.5">{appt.chiefComplaint}</p>
                            )}
                          </div>
                          <Badge
                            variant={
                              appt.status === 'fulfilled' ? 'success'
                                : appt.status === 'cancelled' ? 'destructive'
                                : 'secondary'
                            }
                            size="sm"
                          >
                            {appt.status || 'booked'}
                          </Badge>
                        </div>

                        {/* Action row */}
                        {appt.status !== 'fulfilled' && appt.status !== 'cancelled' && (
                          <div className="flex gap-2 mt-3 pt-3 border-t border-slate-700">
                            <button
                              onClick={() =>
                                setActionApptId((prev) =>
                                  prev === `reschedule-${appt.id}` ? null : `reschedule-${appt.id}`,
                                )
                              }
                              className="flex items-center gap-1 px-3 py-1.5 text-xs text-blue-300 bg-blue-500/10 border border-blue-500/30 rounded-lg hover:bg-blue-500/20 transition-colors"
                            >
                              <Clock className="w-3 h-3" />
                              Reschedule
                            </button>
                            <button
                              onClick={() =>
                                setActionApptId((prev) =>
                                  prev === `cancel-${appt.id}` ? null : `cancel-${appt.id}`,
                                )
                              }
                              className="flex items-center gap-1 px-3 py-1.5 text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg hover:bg-red-500/20 transition-colors"
                            >
                              <X className="w-3 h-3" />
                              Cancel
                            </button>
                          </div>
                        )}

                        {/* Reschedule inline form */}
                        {actionApptId === `reschedule-${appt.id}` && (
                          <div className="mt-3 pt-3 border-t border-slate-700 space-y-2">
                            <p className="text-xs font-semibold text-slate-300">New Time</p>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="text-xs text-slate-400">New Start</label>
                                <input
                                  type="datetime-local"
                                  value={rescheduleForm.start}
                                  onChange={(e) =>
                                    setRescheduleForm((p) => ({ ...p, start: e.target.value }))
                                  }
                                  className="w-full bg-slate-700 border border-slate-600 rounded px-2 py-1.5 text-xs text-white focus:outline-none"
                                />
                              </div>
                              <div>
                                <label className="text-xs text-slate-400">New End</label>
                                <input
                                  type="datetime-local"
                                  value={rescheduleForm.end}
                                  onChange={(e) =>
                                    setRescheduleForm((p) => ({ ...p, end: e.target.value }))
                                  }
                                  className="w-full bg-slate-700 border border-slate-600 rounded px-2 py-1.5 text-xs text-white focus:outline-none"
                                />
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleReschedule(appt.id || appt.appointmentId)}
                                disabled={actionLoading}
                                className="flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg"
                              >
                                {actionLoading && <Loader2 className="w-3 h-3 animate-spin" />}
                                Confirm
                              </button>
                              <button
                                onClick={() => setActionApptId(null)}
                                className="px-3 py-1.5 text-xs text-slate-400 bg-slate-700 hover:bg-slate-600 rounded-lg"
                              >
                                Discard
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Cancel inline form */}
                        {actionApptId === `cancel-${appt.id}` && (
                          <div className="mt-3 pt-3 border-t border-slate-700 space-y-2">
                            <p className="text-xs font-semibold text-slate-300">Cancellation Reason (optional)</p>
                            <input
                              type="text"
                              value={cancelReason}
                              onChange={(e) => setCancelReason(e.target.value)}
                              placeholder="e.g. Patient no-show"
                              className="w-full bg-slate-700 border border-slate-600 rounded px-2 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none"
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleCancel(appt.id || appt.appointmentId)}
                                disabled={actionLoading}
                                className="flex items-center gap-1 px-3 py-1.5 text-xs bg-red-700 hover:bg-red-800 disabled:opacity-50 text-white rounded-lg"
                              >
                                {actionLoading && <Loader2 className="w-3 h-3 animate-spin" />}
                                Confirm Cancel
                              </button>
                              <button
                                onClick={() => setActionApptId(null)}
                                className="px-3 py-1.5 text-xs text-slate-400 bg-slate-700 hover:bg-slate-600 rounded-lg"
                              >
                                Discard
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Patient join link display */}
                        {appt.patientJoinLink && (
                          <div className="mt-3">
                            <JoinLinkCard label="Patient Join Link" link={appt.patientJoinLink} />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
